import { createColumnHelper, tableFeatures, useTable, type RowData } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { CSSProperties, FunctionComponent, ReactNode } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";
import { Empty } from "./empty";
import { Spin } from "./spin";

export interface DataTableColumn<T extends RowData> {
  key: string;
  title?: ReactNode;
  /** Cell content for a row. */
  render: (record: T, index: number) => ReactNode;
  /** px number or any CSS width (e.g. `"30%"`). */
  width?: number | string;
  align?: "left" | "center" | "right";
  /** Single line, truncated with an ellipsis (antd `ellipsis`). */
  ellipsis?: boolean;
}

export interface DataTablePagination {
  pageSize: number;
  /** Server-side paging: the controlled page (1-based) and the full row count. */
  current?: number;
  total?: number;
  onChange?: (page: number) => void;
  /** Hide the pager when everything fits on one page. */
  hideOnSinglePage?: boolean;
}

export interface DataTableProps<T extends RowData> {
  columns: DataTableColumn<T>[];
  data: readonly T[];
  rowKey: (record: T) => string;
  loading?: boolean;
  /** `false` renders every row. */
  pagination?: DataTablePagination | false;
  size?: "small" | "middle";
  /** Shown when `data` is empty. Default `<Empty />`. */
  emptyContent?: ReactNode;
  className?: string;
}

const features = tableFeatures({});

const ALIGN = { left: "text-left", center: "text-center", right: "text-right" } as const;

const CELL_PADDING = { small: "px-2 py-2", middle: "px-2 py-3" } as const;

const widthStyle = (width?: number | string): CSSProperties | undefined =>
  width === undefined ? undefined : { width: typeof width === "number" ? `${width}px` : width };

/** Headless TanStack Table rendered as a plain `<table>` (antd `Table` replacement). */
export function DataTable<T extends RowData>({
  columns,
  data,
  rowKey,
  loading = false,
  pagination,
  size = "middle",
  emptyContent,
  className,
}: DataTableProps<T>) {
  const [localPage, setLocalPage] = useState(1);
  const paged = pagination !== false && pagination !== undefined;
  const serverSide = paged && pagination.total !== undefined;
  const pageSize = paged ? pagination.pageSize : data.length;
  const total = serverSide ? pagination.total! : data.length;
  const pageCount = paged ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  // Clamp so a shrinking dataset never strands the user on an empty page.
  const page = paged ? Math.min(pagination.current ?? localPage, pageCount) : 1;

  const rows = useMemo(
    () => (paged && !serverSide ? data.slice((page - 1) * pageSize, page * pageSize) : [...data]),
    [data, paged, serverSide, page, pageSize]
  );

  const tableColumns = useMemo(() => {
    const helper = createColumnHelper<typeof features, T>();
    return helper.columns(
      columns.map((column) =>
        helper.display({
          id: column.key,
          header: () => column.title,
          cell: ({ row }) => column.render(row.original, row.index),
        })
      )
    );
  }, [columns]);

  const table = useTable({ features, columns: tableColumns, data: rows, getRowId: (record) => rowKey(record) });
  const byKey = new Map(columns.map((column) => [column.key, column]));

  const changePage = (next: number) => {
    setLocalPage(next);
    if (paged) pagination.onChange?.(next);
  };

  const showPager = paged && !(pagination.hideOnSinglePage && pageCount <= 1) && total > 0;

  return (
    <div className={cn("text-sm text-noa", className)}>
      <Spin spinning={loading}>
        <div className="overflow-x-auto">
          {/* Fixed layout only when a column truncates — auto layout never ellipsizes. */}
          <table className={cn("w-full border-collapse", columns.some((column) => column.ellipsis) && "table-fixed")}>
            <thead>
              {table.getHeaderGroups().map((group) => (
                <tr key={group.id}>
                  {group.headers.map((header) => {
                    const column = byKey.get(header.column.id);
                    return (
                      <th
                        key={header.id}
                        scope="col"
                        style={widthStyle(column?.width)}
                        className={cn(
                          "border-b border-noa bg-noa-hover font-semibold first:rounded-tl-lg last:rounded-tr-lg",
                          CELL_PADDING[size],
                          ALIGN[column?.align ?? "left"]
                        )}
                      >
                        {header.isPlaceholder ? null : <table.FlexRender header={header} />}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="transition-colors hover:bg-noa-hover">
                  {row.getAllCells().map((cell) => {
                    const column = byKey.get(cell.column.id);
                    return (
                      <td
                        key={cell.id}
                        className={cn(
                          "border-b border-noa align-middle",
                          CELL_PADDING[size],
                          ALIGN[column?.align ?? "left"],
                          column?.ellipsis && "truncate"
                        )}
                      >
                        <table.FlexRender cell={cell} />
                      </td>
                    );
                  })}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="border-b border-noa">
                    {emptyContent ?? <Empty />}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Spin>
      {showPager && <Pager page={page} pageCount={pageCount} onChange={changePage} />}
    </div>
  );
}

interface PagerProps {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}

/** Page numbers around the current one, with the first/last always shown (`null` = gap). */
const visiblePages = (page: number, pageCount: number): (number | null)[] => {
  const pages = Array.from({ length: pageCount }, (_, index) => index + 1).filter(
    (candidate) => candidate === 1 || candidate === pageCount || Math.abs(candidate - page) <= 2
  );
  return pages.flatMap((candidate, index) =>
    index > 0 && candidate - pages[index - 1] > 1 ? [null, candidate] : [candidate]
  );
};

const PAGER_BUTTON = cn(
  FOCUS_RING,
  "inline-flex h-8 min-w-8 cursor-pointer items-center justify-center rounded-md border border-transparent bg-transparent px-1.5 text-sm text-noa transition-colors hover:bg-noa-hover disabled:cursor-not-allowed disabled:text-noa-disabled disabled:hover:bg-transparent"
);

const Pager: FunctionComponent<PagerProps> = ({ page, pageCount, onChange }) => {
  const { t } = useTranslation();

  return (
    <nav aria-label={t("common:pagination")} className="mt-4 flex items-center justify-end gap-2">
      <button
        type="button"
        aria-label={t("common:previous")}
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className={PAGER_BUTTON}
      >
        <ChevronLeft className="size-3.5" />
      </button>
      {visiblePages(page, pageCount).map((candidate, index) =>
        candidate === null ? (
          <span key={`gap-${index}`} className="text-noa-muted">
            …
          </span>
        ) : (
          <button
            key={candidate}
            type="button"
            aria-label={t("common:pageNumber", { page: candidate })}
            aria-current={candidate === page ? "page" : undefined}
            onClick={() => onChange(candidate)}
            className={cn(
              PAGER_BUTTON,
              candidate === page && "border-noa-primary-strong font-semibold text-noa-primary"
            )}
          >
            {candidate}
          </button>
        )
      )}
      <button
        type="button"
        aria-label={t("common:next")}
        disabled={page >= pageCount}
        onClick={() => onChange(page + 1)}
        className={PAGER_BUTTON}
      >
        <ChevronRight className="size-3.5" />
      </button>
    </nav>
  );
};
